import {
  BILLING_MODULE_KEYS,
  type BillingModuleKey,
} from '@bill-alps/backend/features/companies/modules';
import {
  FIDUCIAIRE_USAGE_FEATURES,
  fiduciaireModuleMonthly,
  fiduciaireModuleRate,
  fiduciaireMonthlyTotal,
  type FiduciaireUsageFeature,
} from '@bill-alps/backend/features/fiduciaire/plans';
import { FIDUCIAIRE_USAGE_MODULE } from '@bill-alps/backend/features/fiduciaire/usage';

/** Billed mandates per module — the shape `getMyFiduciaire` returns. */
export type ModuleQuantities = Partial<Record<BillingModuleKey, number>>;

export interface ModuleBillingLine {
  module: BillingModuleKey;
  quantity: number;
  /** CHF per mandate per month at that quantity's tier. */
  rate: number;
  /** quantity × rate, rounded to Rappen. */
  monthly: number;
}

/**
 * What the subscription would bill per module: one line per module with at
 * least one billed mandate, in canonical module order.
 */
export function moduleBillingLines(
  quantities: ModuleQuantities,
): ModuleBillingLine[] {
  return BILLING_MODULE_KEYS.flatMap((module) => {
    const quantity = quantities[module] ?? 0;
    if (quantity <= 0) return [];
    return [
      {
        module,
        quantity,
        rate: fiduciaireModuleRate(quantity),
        monthly: fiduciaireModuleMonthly(quantity),
      },
    ];
  });
}

/**
 * The quantities after a mandate's module set changes: `add` gains one
 * billed mandate per module, `remove` loses one. A brand-new mandate passes
 * only `add`; a module change passes the old set as `remove`.
 */
export function projectModuleQuantities(
  current: ModuleQuantities,
  add: readonly BillingModuleKey[],
  remove: readonly BillingModuleKey[] = [],
): ModuleQuantities {
  const next: ModuleQuantities = { ...current };
  for (const module of remove) {
    next[module] = Math.max(0, (next[module] ?? 0) - 1);
  }
  for (const module of add) next[module] = (next[module] ?? 0) + 1;
  return next;
}

export interface BillingEstimate {
  before: number;
  after: number;
  /** after − before; negative when the change lowers the bill. */
  delta: number;
  lines: ModuleBillingLine[];
}

/**
 * Live estimate shown while a trustee picks a mandate's modules. Volume
 * tiers make the delta non-linear — crossing into a cheaper tier can lower
 * the total for every mandate of that module — so it is computed from the
 * whole projected quantity set, never from a per-mandate unit price.
 */
export function estimateBillingChange(
  current: ModuleQuantities,
  add: readonly BillingModuleKey[],
  remove: readonly BillingModuleKey[] = [],
): BillingEstimate {
  const projected = projectModuleQuantities(current, add, remove);
  const before = fiduciaireMonthlyTotal(current);
  const after = fiduciaireMonthlyTotal(projected);
  return {
    before,
    after,
    delta: Math.round((after - before) * 100) / 100,
    lines: moduleBillingLines(projected),
  };
}

/** The metered features a billed module set covers, in canonical order. */
export function usageFeaturesFor(
  modules: readonly BillingModuleKey[],
): FiduciaireUsageFeature[] {
  const covered = new Set(modules);
  return FIDUCIAIRE_USAGE_FEATURES.filter((feature) =>
    covered.has(FIDUCIAIRE_USAGE_MODULE[feature]),
  );
}
